import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutList, RefreshCw, LayoutDashboard, Code2, Columns3, Filter, Plus, X, Building2, Loader2,
  Phone, Mail, MoreHorizontal, Eye, MessageCircle, Sparkles, Send,
} from "lucide-react";

import { usePipelineData, useBulkActions } from "./AdminDashboard.hooks";
import { BusinessEmptyState } from "./AdminDashboard.BusinessCard";
import { PipelineFilterDrawer } from "./AdminDashboard.FilterDrawer";
import { FilterTabs, BulkActionBar, SelectAllRow, PipelineToolbar } from "./AdminDashboard.Pipeline";
import HealthSection from "./AdminDashboard.HealthSection";
import { KanbanView } from "./AdminDashboard.KanbanView";
import { WidgetsSection } from "./AdminDashboard.WidgetsSection";
import QuickEmailModal from "../components/QuickEmailModal";
import { authFetch, API } from "../utils/api";

/* ── Status badge config (alineado con AdminDashboard.constants.STATUS_LABELS) */
const STATUS_BADGE = {
  new:            { label: "Nuevo",       bg: "rgba(255,193,7,0.10)",  color: "#FFC107" },
  researched:     { label: "Investigado", bg: "rgba(49,153,186,0.15)", color: "#3199BA" },
  emailed:        { label: "Seguimiento", bg: "rgba(167,139,250,0.15)",color: "#A78BFA" },
  site_generated: { label: "Sitio Listo", bg: "rgba(255,79,0,0.12)",   color: "#FF4F00" },
  generating_site:{ label: "Generando...",bg: "rgba(255,79,0,0.08)",   color: "#FF4F00" },
  site_error:     { label: "Error sitio", bg: "rgba(239,68,68,0.10)",  color: "#f87171" },
  paid:           { label: "Pagado",      bg: "rgba(52,211,153,0.15)", color: "#34D399" },
  won:            { label: "Ganado",      bg: "rgba(52,211,153,0.15)", color: "#22c55e" },
  lost:           { label: "Perdido",     bg: "rgba(255,115,81,0.10)", color: "#ff7351" },
};

// ── Returns true if a business was created <24h ago AND is still uncontacted
function isFreshLead(biz) {
  if (!biz?.created_at) return false;
  if (biz.status && biz.status !== "new") return false;
  try {
    const ageMs = Date.now() - new Date(biz.created_at).getTime();
    return ageMs >= 0 && ageMs < 24 * 60 * 60 * 1000;
  } catch { return false; }
}

// ── Outreach text builders (stage-aware)
// Produces { subject, body, whatsapp } based on business status.
function buildOutreachCopy(biz) {
  const nombre = biz.name || "tu empresa";
  const leadFirst = biz.lead_name ? ` ${biz.lead_name.split(" ")[0]}` : "";
  const status = biz.status || "new";
  const preview = biz.preview_url || "";

  const T = {
    new: {
      subject: `Idea para ${nombre} — sitio web en 24h`,
      body: `Hola${leadFirst},

Vi ${nombre} y pensé que una web profesional puede sumar bastante a la imagen y captación.

En Sitios YA armamos sitios listos en 24h por USD 20/mes, sin permanencia. ¿Te mando un preview gratis para que lo veas?

Saludos,
Sitios YA`,
      whatsapp: `Hola${leadFirst}! Vi ${nombre} y creo que una web profesional te sumaría clientes. ¿Te mando un preview gratis?`,
    },
    researched: {
      subject: `${nombre} — preview listo en 24h`,
      body: `Hola${leadFirst},

Estuve investigando ${nombre} y preparé una propuesta concreta de sitio web (demo en 24h, USD 20/mes, sin permanencia).

¿Te interesa que te mande el preview para que veas cómo quedaría?

Saludos,
Sitios YA`,
      whatsapp: `Hola${leadFirst}! Preparé una propuesta para ${nombre}. ¿Te paso el preview?`,
    },
    emailed: {
      subject: `Seguimiento — ${nombre}`,
      body: `Hola${leadFirst},

¿Pudiste ver la propuesta que te mandé? Quería confirmar que llegó bien y ver si tenés dudas.

Si preferís, puedo llamarte 5 min y te cuento de qué se trata.

Saludos,
Sitios YA`,
      whatsapp: `Hola${leadFirst}! ¿Pudiste ver la propuesta de ${nombre}? ¿Dudas?`,
    },
    site_generated: {
      subject: `Tu sitio de ${nombre} está listo para ver`,
      body: `Hola${leadFirst},

Ya terminamos la demo de tu sitio${preview ? `: ${preview}` : ""}.

Entra, revisala y decime qué te parece. Si querés lo publicamos hoy mismo por USD 20/mes.

Saludos,
Sitios YA`,
      whatsapp: `Hola${leadFirst}! Te dejé la demo del sitio de ${nombre}${preview ? `: ${preview}` : ""}. ¿Qué te parece?`,
    },
    paid: {
      subject: `¡Bienvenido a Sitios YA, ${nombre}!`,
      body: `Hola${leadFirst},

¡Gracias por confiar en nosotros! Ya estás dentro.

Te mandé por separado el acceso al panel para que edites tu web cuando quieras. Cualquier duda, me avisás.

Saludos,
Sitios YA`,
      whatsapp: `Hola${leadFirst}! Bienvenido a Sitios YA. Cualquier duda con el panel me avisás.`,
    },
    won: null, // reuse paid
    lost: {
      subject: `${nombre} — ¿lo volvemos a intentar?`,
      body: `Hola${leadFirst},

Sé que la última vez no avanzamos, pero me pareció importante volver a escribirte.

Si cambió algo en tu situación y querés revisar el sitio que armamos para ${nombre}, acá estoy.

Saludos,
Sitios YA`,
      whatsapp: `Hola${leadFirst}! ¿Retomamos lo de ${nombre}?`,
    },
  };
  return T[status] || T.won || T.paid || T.new;
}

// Backward compatible helpers used by legacy sites of this file
function buildOutreachSubject(biz) { return buildOutreachCopy(biz).subject; }
function buildOutreachBody(biz)    { return buildOutreachCopy(biz).body; }

/**
 * Reusable quick-outreach popup. Can be triggered from any icon/button.
 * Opens stage-aware menu with:
 *  - "Enviar email" → opens in-app modal (Gmail SMTP + Inbox log + auto-advance stage)
 *  - "WhatsApp" → short click-to-chat via wa.me
 */
function QuickActionsPopup({ biz, open, onClose, anchorRef, onEmailClick }) {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const copy = buildOutreachCopy(biz);
  const whatsapp = biz.phone
    ? `https://wa.me/${String(biz.phone).replace(/\D/g, "")}?text=${encodeURIComponent(copy.whatsapp)}`
    : null;

  const stop = (e) => e.stopPropagation();

  return (
    <div
      ref={wrapRef}
      data-testid={`outreach-menu-${biz.id}`}
      className="absolute z-50 top-full right-0 mt-1.5 w-[240px] max-w-[calc(100vw-32px)] rounded-xl overflow-hidden shadow-2xl animate-fadeInUp"
      style={{ background: "#141414", border: "1px solid rgba(255,193,7,0.30)" }}
      onClick={stop}
    >
      <div className="px-3 py-2 flex items-center gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Sparkles size={11} style={{ color: "#FFC107" }} />
        <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: "#FFC107" }}>
          Contactar ahora
        </span>
      </div>

      <button
        data-testid={`outreach-email-${biz.id}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!biz.email) { toast.error("Sin email registrado"); return; }
          // Navigate to Inbox with pre-filled composer (preview before sending)
          onClose();
          window.location.assign(`/inbox?biz=${biz.id}&compose=1`);
        }}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-semibold transition-colors text-left ${biz.email ? "text-white hover:bg-white/5" : "text-gray-600 cursor-not-allowed"}`}
        disabled={!biz.email}
      >
        <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "rgba(52,211,153,0.15)" }}>
          <Mail size={12} style={{ color: "#34D399" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="leading-tight">Enviar email</p>
          <p className="text-[10px] font-normal truncate" style={{ color: "var(--ds-text-muted)" }}>
            {biz.email || "sin email"}
          </p>
        </div>
      </button>

      <a
        data-testid={`outreach-whatsapp-${biz.id}`}
        href={whatsapp || "#"}
        onClick={(e) => {
          e.stopPropagation();
          if (!whatsapp) { e.preventDefault(); toast.error("Sin teléfono registrado"); return; }
          toast.success("Abriendo WhatsApp...");
          onClose();
        }}
        target="_blank"
        rel="noreferrer"
        className={`flex items-center gap-2.5 px-3 py-2.5 text-xs font-semibold transition-colors ${whatsapp ? "text-white hover:bg-white/5" : "text-gray-600 cursor-not-allowed"}`}
      >
        <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "rgba(37,211,102,0.18)" }}>
          <MessageCircle size={12} style={{ color: "#25D366" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="leading-tight">WhatsApp</p>
          <p className="text-[10px] font-normal truncate" style={{ color: "var(--ds-text-muted)" }}>
            {biz.phone || "sin teléfono"}
          </p>
        </div>
      </a>
    </div>
  );
}

/**
 * The 24H pill (fresh leads only). Same popup as QuickActionsPopup.
 */
function FreshLeadPill({ biz, variant = "desktop", onEmailClick }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  const sizing = variant === "desktop" ? "text-[9px] px-1.5 py-1" : "text-[9px] px-1.5 py-0.5";

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        data-testid={variant === "desktop" ? `fresh-pill-desktop-${biz.id}` : `fresh-pill-${biz.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        title="Contactar ahora (menos de 24h sin acción)"
        aria-label="Acciones rápidas de outreach"
        aria-expanded={open}
        className={`${sizing} font-black rounded-md uppercase tracking-wide whitespace-nowrap transition-all cursor-pointer hover:scale-[1.08] ${open ? "" : "animate-pulse"}`}
        style={{
          background: open ? "#FFC107" : "rgba(255,193,7,0.18)",
          color: open ? "#0A0A0A" : "#FFC107",
          border: open ? "1px solid #FFC107" : "1px solid rgba(255,193,7,0.35)",
        }}
      >
        24H
      </button>
      <QuickActionsPopup biz={biz} open={open} onClose={() => setOpen(false)} anchorRef={btnRef} onEmailClick={onEmailClick} />
    </div>
  );
}

/**
 * Always-visible quick-action icon for every row (not only fresh leads).
 * Small Send icon that opens the same stage-aware popup.
 */
function RowQuickActionButton({ biz, onEmailClick, variant = "desktop" }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const testId = variant === "desktop" ? `quick-action-btn-${biz.id}` : `quick-action-btn-mobile-${biz.id}`;
  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        data-testid={testId}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        title="Contactar (email / WhatsApp)"
        aria-label="Acción rápida de outreach"
        className="p-2 rounded-lg transition-colors"
        style={{
          color: open ? "#FFC107" : "#9ca3af",
          background: open ? "rgba(255,193,7,0.12)" : "transparent",
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = "transparent"; }}
      >
        <Send size={14} />
      </button>
      <QuickActionsPopup biz={biz} open={open} onClose={() => setOpen(false)} anchorRef={btnRef} onEmailClick={onEmailClick} />
    </div>
  );
}

function relativeTime(dateStr) {
  if (!dateStr) return "—";
  try {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 60000);
    if (diff < 1) return "Ahora";
    if (diff < 60) return `Hace ${diff} min`;
    if (diff < 1440) return `Hace ${Math.floor(diff / 60)}h`;
    return `Hace ${Math.floor(diff / 1440)}d`;
  } catch { return "—"; }
}

/* ── Pipeline Table Row ─────────────────────────────────────────────────────── */
function PipelineTableRow({ biz, selected, onToggle, onClick, offerPrice, onEmailClick }) {
  const badge = STATUS_BADGE[biz.status] || STATUS_BADGE.new;
  const fresh = isFreshLead(biz);
  const initials = (biz.name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <div
      data-testid={`pipeline-row-${biz.id}`}
      className="group block lg:grid items-center px-3 sm:px-4 lg:px-6 py-3 lg:py-4 transition-colors cursor-pointer lg:gap-0"
      style={{
        gridTemplateColumns: "40px minmax(160px,2fr) 110px minmax(90px,120px) minmax(90px,120px) minmax(80px,1fr) 70px",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        background: selected ? "rgba(255,193,7,0.06)" : "transparent",
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = "rgba(255,255,255,0.025)"; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = "transparent"; }}
      onClick={onClick}
    >
      {/* ── Checkbox column (visible on all breakpoints) ── */}
      <div className="hidden lg:flex items-center justify-center">
        <button
          data-testid={`row-select-${biz.id}`}
          onClick={handleCheckboxClick}
          aria-label={selected ? "Deseleccionar" : "Seleccionar"}
          aria-pressed={selected}
          className="w-5 h-5 rounded-md flex items-center justify-center transition-colors"
          style={{
            background: selected ? "#FFC107" : "transparent",
            border: selected ? "1px solid #FFC107" : "1px solid rgba(255,255,255,0.20)",
          }}
        >
          {selected && (
            <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 10 8 14 16 6" />
            </svg>
          )}
        </button>
      </div>

      {/* ── Name column (mobile-first stacked layout) ── */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Mobile-only checkbox (stays visible) */}
        <button
          data-testid={`row-select-mobile-${biz.id}`}
          onClick={handleCheckboxClick}
          aria-label={selected ? "Deseleccionar" : "Seleccionar"}
          aria-pressed={selected}
          className="lg:hidden flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-colors"
          style={{
            background: selected ? "#FFC107" : "transparent",
            border: selected ? "1px solid #FFC107" : "1px solid rgba(255,255,255,0.20)",
          }}
        >
          {selected && (
            <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 10 8 14 16 6" />
            </svg>
          )}
        </button>
        <div className="flex-shrink-0">
          <div
            className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center text-xs lg:text-sm font-bold text-white"
            style={{ background: selected ? "rgba(255,193,7,0.20)" : "#262626" }}
          >
            {initials}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 justify-between lg:justify-start">
            <p className="font-bold text-white truncate text-sm">{biz.name}</p>
            {/* Status inline on <lg next to name */}
            <div className="lg:hidden flex-shrink-0 flex items-center gap-1">
              {fresh && <FreshLeadPill biz={biz} variant="mobile" onEmailClick={onEmailClick} />}
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-md"
                style={{ background: badge.bg, color: badge.color }}
              >
                {badge.label}
              </span>
            </div>
          </div>
          <p className="text-xs truncate" style={{ color: "var(--ds-text-muted)" }}>{biz.email || biz.phone || biz.city || "—"}</p>
          {/* <lg compact meta line (without action button — it's at row-end now) */}
          <div className="lg:hidden flex items-center gap-2 mt-1.5 text-[11px] flex-wrap" style={{ color: "var(--ds-text-muted)" }}>
            <span className="font-mono font-bold text-white">{offerPrice}</span>
            <span>·</span>
            <span>{relativeTime(biz.updated_at || biz.last_contact || biz.created_at)}</span>
            {biz.category && <><span>·</span><span className="truncate max-w-[140px]">{biz.category}</span></>}
          </div>
        </div>
        {/* Mobile-only: quick-action button at the END of the row (right-aligned) */}
        <div className="lg:hidden flex-shrink-0 ml-auto">
          <RowQuickActionButton biz={biz} onEmailClick={onEmailClick} variant="mobile" />
        </div>
      </div>

      {/* Status (≥lg) */}
      <div className="hidden lg:flex items-center gap-1.5 flex-wrap">
        {fresh && <FreshLeadPill biz={biz} variant="desktop" onEmailClick={onEmailClick} />}
        <span
          className="text-xs font-bold px-2.5 py-1 rounded-lg whitespace-nowrap"
          style={{ background: badge.bg, color: badge.color }}
        >
          {badge.label}
        </span>
      </div>

      {/* Valor estimado (≥lg) */}
      <div className="hidden lg:block">
        <span className="text-sm font-mono font-bold text-white whitespace-nowrap">
          {offerPrice}
        </span>
      </div>

      {/* Última actividad (≥lg) */}
      <div className="hidden lg:block">
        <p className="text-xs truncate" style={{ color: "var(--ds-text-muted)" }}>
          {relativeTime(biz.updated_at || biz.last_contact || biz.created_at)}
        </p>
      </div>

      {/* Categoría (≥lg) */}
      <div className="hidden lg:block min-w-0">
        <span className="text-xs truncate block" style={{ color: "var(--ds-text-muted)" }}>{biz.category || "—"}</span>
      </div>

      {/* Actions (≥lg, quick-action always visible; others on hover) */}
      <div className="hidden lg:flex items-center gap-1 justify-end">
        <RowQuickActionButton biz={biz} onEmailClick={onEmailClick} />
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          <button
            onClick={e => { e.stopPropagation(); onClick(); }}
            className="p-2 rounded-lg transition-colors hover:bg-white/10"
            style={{ color: "#9ca3af" }}
            title="Ver detalle"
          >
            <Eye size={14} />
          </button>
          {biz.phone && (
            <a
              href={`tel:${biz.phone}`}
              onClick={e => e.stopPropagation()}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              style={{ color: "#9ca3af" }}
              title="Llamar"
            >
              <Phone size={14} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();

  // ── Data ──────────────────────────────────────────────────────────────────
  const {
    businesses, analytics, loading, users,
    pendingWidgetsCount, setPendingWidgetsCount, fetchData,
  } = usePipelineData();

  // ── Settings (offer price) ─────────────────────────────────────────────────
  const [settings, setSettings] = useState({ offer_price: 20, offer_currency: "USD" });
  useEffect(() => {
    authFetch(`${API}/settings`).then(r => r?.json()).then(d => { if (d) setSettings(d); }).catch(() => {});
  }, []);

  const formatOfferPrice = (biz) => {
    if (biz.estimated_value) return `$${Number(biz.estimated_value).toLocaleString("es-UY")}`;
    const price = settings.offer_price || 20;
    const cur = settings.offer_currency || "USD";
    return `${cur === "USD" ? "$" : ""}${Number(price).toLocaleString("es-UY")}${cur !== "USD" ? ` ${cur}` : "/mo"}`;
  };

  // ── UI state ──────────────────────────────────────────────────────────────
  const [filter,          setFilter]          = useState("all");
  const [viewMode,        setViewMode]        = useState("list");
  const [filterAssigned,  setFilterAssigned]  = useState("");
  const [activeTab,       setActiveTab]       = useState("pipeline");
  const [showAddBusiness, setShowAddBusiness] = useState(false);
  const [search,          setSearch]          = useState("");
  const [sortOrder,       setSortOrder]       = useState("newest");

  // ── Derived list ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = filter === "all" ? businesses : businesses.filter(b => b.status === filter);
    if (filterAssigned) list = list.filter(b => b.assigned_to === filterAssigned);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(b =>
        [b.name, b.phone, b.email, b.category, b.city].some(f => (f || "").toLowerCase().includes(q))
      );
    }
    // Sort
    return [...list].sort((a, b) => {
      if (sortOrder === "az")         return (a.name || "").localeCompare(b.name || "");
      if (sortOrder === "za")         return (b.name || "").localeCompare(a.name || "");
      if (sortOrder === "oldest")     return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sortOrder === "valor_desc") return (b.estimated_value || 0) - (a.estimated_value || 0);
      if (sortOrder === "valor_asc")  return (a.estimated_value || 0) - (b.estimated_value || 0);
      // newest (default): already sorted desc by backend, maintain order
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [businesses, filter, filterAssigned, search, sortOrder]);

  const kanbanBusinesses = useMemo(() => (
    filterAssigned ? businesses.filter(b => b.assigned_to === filterAssigned) : businesses
  ), [businesses, filterAssigned]);

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const bulk = useBulkActions(filtered, fetchData);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--ds-surface)", fontFamily: "'Inter', sans-serif" }}>
      <DashboardHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        analytics={analytics}
        pendingWidgetsCount={pendingWidgetsCount}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onRefresh={fetchData}
        onAddBusiness={() => setShowAddBusiness(true)}
      />

      {showAddBusiness && (
        <AddBusinessModal
          onClose={() => setShowAddBusiness(false)}
          onDone={() => { setShowAddBusiness(false); fetchData(); }}
        />
      )}

      {/* Tab: Widgets */}
      {activeTab === "widgets" && (
        <WidgetsSection onRefresh={() => {
          authFetch(`${API}/admin/widgets/pending`)
            .then(r => r?.json())
            .then(d => setPendingWidgetsCount((d?.widgets || []).length))
            .catch(() => {});
        }} />
      )}

      {/* Tab: Health (Iter90) — provider status & error log */}
      {activeTab === "health" && <HealthSection />}

      {/* Tab: Pipeline */}
      {activeTab === "pipeline" && (
        <>
          {/* KPI Cards — Stitch Design */}
          <div className="px-3 sm:px-6 lg:px-10 pb-6 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            {[
              { label: "Total Leads",         value: analytics.total || 0,               suffix: "",   delta: "+12%",  positive: true,  color: "#FFC107" },
              { label: "Nuevos Hoy",           value: analytics.today_count || 0,         suffix: "",   delta: null,    positive: true,  color: "#3199BA" },
              { label: "Tasa de Cierre",       value: `${analytics.conversion_rate || 0}%`, suffix: "", delta: null,    positive: true,  color: "#34D399" },
              { label: "Ingresos Proyectados", value: `$${((analytics.paid || 0) * 20).toLocaleString()}`, suffix: "", delta: null, positive: true, color: "#A78BFA" },
            ].map(({ label, value, delta, positive, color }) => (
              <div key={label} className="rounded-2xl p-4 lg:p-6 flex flex-col gap-2" style={{ background: "var(--ds-surface-card)" }}>
                <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--ds-text-muted)" }}>{label}</p>
                <p className="text-2xl lg:text-3xl font-black mt-1 truncate" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", color }}>{value}</p>
                {delta && (
                  <span className="text-xs font-bold flex items-center gap-1" style={{ color: positive ? "#34D399" : "var(--ds-error)" }}>
                    {positive ? "▲" : "▼"} {delta}
                  </span>
                )}
              </div>
            ))}
          </div>

          <FilterTabs filter={filter} setFilter={setFilter} />

          {bulk.someSelected && (
            <BulkActionBar
              selected={bulk.selected}
              bulkLoading={bulk.bulkLoading}
              showStatusDrop={bulk.showStatusDrop}
              setShowStatusDrop={bulk.setShowStatusDrop}
              bulkSetStatus={bulk.bulkSetStatus}
              bulkDelete={bulk.bulkDelete}
              onClear={bulk.clearSelected}
            />
          )}

          {viewMode === "kanban" ? (
            <KanbanView
              businesses={kanbanBusinesses}
              onNavigate={(id) => navigate(`/business/${id}`)}
              onRefresh={fetchData}
            />
          ) : (
            <div className="px-3 sm:px-6 lg:px-10 pb-10">
              {/* Toolbar: SelectAll + Search + Sort */}
              <PipelineToolbar
                allSelected={bulk.allSelected}
                filteredCount={filtered.length}
                onSelectAll={bulk.selectAll}
                search={search}
                onSearch={setSearch}
                sortOrder={sortOrder}
                onSort={setSortOrder}
              />

              <div
                className="overflow-hidden rounded-3xl shadow-2xl"
                style={{ background: "var(--ds-surface-card)", border: "1px solid rgba(255,255,255,0.05)" }}
              >
                {/* Table header */}
                <div
                  className="hidden lg:grid items-center px-6 py-4 text-[10px] font-bold uppercase tracking-[0.2em]"
                  style={{
                    gridTemplateColumns: "40px minmax(160px,2fr) 110px minmax(90px,120px) minmax(90px,120px) minmax(80px,1fr) 70px",
                    background: "rgba(255,255,255,0.03)",
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                    color: "var(--ds-text-muted)",
                  }}
                >
                  <span />
                  <span>Nombre / Prospecto</span>
                  <span>Estado</span>
                  <span>Valor</span>
                  <span>Últ. Actividad</span>
                  <span>Categoría</span>
                  <span />
                </div>

                {/* Table body */}
                {loading ? (
                  <div className="py-16 text-center" style={{ color: "var(--ds-text-muted)" }}>
                    <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                    <p className="text-sm">Cargando leads...</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="py-16 text-center">
                    {search ? (
                      <>
                        <p className="text-sm font-bold" style={{ color: "var(--ds-text-muted)" }}>Sin resultados para "{search}"</p>
                        <button onClick={() => setSearch("")} className="text-xs mt-2 hover:underline" style={{ color: "var(--ds-primary)" }}>Limpiar búsqueda</button>
                      </>
                    ) : (
                      <p className="text-sm" style={{ color: "var(--ds-text-muted)" }}>Sin leads para este filtro</p>
                    )}
                  </div>
                ) : (
                  filtered.map((biz) => (
                    <PipelineTableRow
                      key={biz.id}
                      biz={biz}
                      selected={bulk.selected.has(biz.id)}
                      onToggle={() => bulk.toggleSelect(biz.id)}
                      onClick={() => navigate(`/business/${biz.id}`)}
                      offerPrice={formatOfferPrice(biz)}
                    />
                  ))
                )}

                {!loading && filtered.length > 0 && (
                  <div className="px-3 sm:px-6 py-3 text-xs" style={{ color: "var(--ds-text-muted)", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                    Mostrando {filtered.length} de {businesses.length} contactos
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Add Business Modal ────────────────────────────────────────────────────────

function AddBusinessModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    name: "", category: "", phone: "", email: "", address: "", city: "", website: "", notes: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setLoading(true);
    try {
      const res = await authFetch(`${API}/businesses`, {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim(),
          category: form.category.trim() || "General",
          phone: form.phone.trim(),
          email: form.email.trim(),
          address: form.address.trim(),
          city: form.city.trim(),
          website: form.website.trim(),
          // Save the typed note in BOTH `description` (legacy AI context) and
          // `note` (timeline) so it's visible in BusinessDetail AND picked up
          // by research / outreach / site generation prompts.
          description: form.notes.trim(),
          note: form.notes.trim(),
          source: "manual",
        }),
      });
      if (!res || !res.ok) throw new Error();
      toast.success("Empresa agregada al pipeline");
      onDone();
    } catch {
      toast.error("Error al agregar la empresa");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div data-testid="add-business-modal"
        className="bg-[#141414] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Building2 size={16} className="text-[#ffc700]" />
            <span className="text-sm font-black text-white">Agregar empresa al Pipeline</span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 flex flex-col gap-3 max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-bold text-gray-400 block mb-1">Nombre <span className="text-red-400">*</span></label>
              <input data-testid="add-biz-name" value={form.name} onChange={e => set("name", e.target.value)}
                placeholder="Ej: Café del Puerto" required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-400 block mb-1">Rubro / Categoría</label>
              <input data-testid="add-biz-category" value={form.category} onChange={e => set("category", e.target.value)}
                placeholder="Ej: Cafetería, Abogado"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-400 block mb-1">Ciudad</label>
              <input value={form.city} onChange={e => set("city", e.target.value)}
                placeholder="Ej: Montevideo"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-400 block mb-1">Teléfono</label>
              <input data-testid="add-biz-phone" value={form.phone} onChange={e => set("phone", e.target.value)}
                placeholder="098 000 000"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-400 block mb-1">Email</label>
              <input data-testid="add-biz-email" value={form.email} onChange={e => set("email", e.target.value)}
                type="email" placeholder="contacto@empresa.com"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-bold text-gray-400 block mb-1">Dirección</label>
              <input value={form.address} onChange={e => set("address", e.target.value)}
                placeholder="Ej: Av. 18 de Julio 1234"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-bold text-gray-400 block mb-1">Sitio web actual (si tiene)</label>
              <input value={form.website} onChange={e => set("website", e.target.value)}
                placeholder="https://www.miempresa.com"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-bold text-gray-400 block mb-1">Notas internas</label>
              <textarea value={form.notes} onChange={e => set("notes", e.target.value)}
                placeholder="Contacto por WhatsApp, referido por Juan..." rows={2}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50 resize-none" />
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="add-biz-submit"
            className="flex items-center justify-center gap-2 w-full py-3 bg-[#ffc700] text-black rounded-xl text-sm font-black hover:bg-yellow-400 transition-all disabled:opacity-50 mt-1">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {loading ? "Agregando..." : "Agregar al Pipeline"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DashboardHeader({
  activeTab, setActiveTab, analytics, pendingWidgetsCount,
  viewMode, setViewMode, onRefresh, onAddBusiness,
}) {
  return (
    <div className="px-4 sm:px-6 lg:px-10 pt-6 lg:pt-8 pb-5 lg:pb-6" style={{ background: "var(--ds-surface)" }}>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-widest mb-1" style={{ color: "var(--ds-primary)" }}>
            {activeTab === "pipeline" ? "CRM ELITE" : "WIDGETS"}
          </p>
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold tracking-tight text-white"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {activeTab === "pipeline" ? "Pipeline de Leads" : "Sitios Generados"}
          </h1>
        </div>

        <div className="flex items-center gap-2 lg:gap-3 flex-wrap">
          {/* Tab switcher */}
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: "var(--ds-surface-low)" }}>
            <button
              data-testid="tab-pipeline-btn"
              onClick={() => setActiveTab("pipeline")}
              className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
              style={activeTab === "pipeline" ? { background: "var(--ds-primary)", color: "#0e0e0e" } : { color: "#6b7280" }}
            >
              Pipeline
            </button>
            <button
              data-testid="tab-widgets-btn"
              onClick={() => setActiveTab("widgets")}
              className="relative px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
              style={activeTab === "widgets" ? { background: "var(--ds-primary)", color: "#0e0e0e" } : { color: "#6b7280" }}
            >
              Widgets
              {pendingWidgetsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center">
                  {pendingWidgetsCount}
                </span>
              )}
            </button>
            <button
              data-testid="tab-health-btn"
              onClick={() => setActiveTab("health")}
              className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
              style={activeTab === "health" ? { background: "var(--ds-primary)", color: "#0e0e0e" } : { color: "#6b7280" }}
            >
              Salud
            </button>
          </div>

          {activeTab === "pipeline" && (
            <div className="flex gap-1 p-1 rounded-xl" style={{ background: "var(--ds-surface-low)" }}>
              <button data-testid="view-list-btn" onClick={() => setViewMode("list")}
                className="p-2 rounded-lg transition-all"
                style={viewMode === "list" ? { background: "rgba(255,255,255,0.10)", color: "#fff" } : { color: "#6b7280" }}>
                <LayoutList size={15} />
              </button>
              <button data-testid="view-kanban-btn" onClick={() => setViewMode("kanban")}
                className="p-2 rounded-lg transition-all"
                style={viewMode === "kanban" ? { background: "rgba(255,255,255,0.10)", color: "#fff" } : { color: "#6b7280" }}>
                <Columns3 size={15} />
              </button>
            </div>
          )}

          <button
            data-testid="add-business-btn"
            onClick={onAddBusiness}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95"
            style={{ background: "linear-gradient(135deg, #ffd16c, #fdc003)", color: "#3d2b00" }}
          >
            <Plus size={14} /> Nuevo Prospecto
          </button>

          <button onClick={onRefresh} className="p-2.5 rounded-xl transition-all" style={{ background: "var(--ds-surface-card)" }}>
            <RefreshCw size={15} style={{ color: "#6b7280" }} />
          </button>
        </div>
      </div>
    </div>
  );
}
